<template>
    <BaseDialog v-model:open="isOpen" title="Your self-review">
        <p class="body-small text-ink-muted">
            Drafted from your conversation. Edit each comment, then paste it into the matching section of your self-evaluation in Workday. Edits aren't saved when you close this
            window.
        </p>

        <section v-for="(section, index) in localSections" :key="index" class="flex flex-col gap-1">
            <div class="flex items-center justify-between gap-2">
                <h4 class="body-semibold text-ink">{{ section.name }}</h4>
                <BaseButton
                    theme="tertiary"
                    :data-testid="`copy-${index}`"
                    :disabled="!section.comment.trim()"
                    class="flex items-center gap-1"
                    @click="copy(section.comment, String(index), index)">
                    <Copy class="w-3 h-3" aria-hidden="true" />
                    {{ isCopied && copiedKey === String(index) ? "Copied!" : "Copy" }}
                </BaseButton>
            </div>
            <BaseTextarea
                v-model:input="section.comment"
                rows="3"
                :data-testid="`edit-${index}`"
                :aria-label="section.name"
                :placeholder="`Add a comment for ${section.name}.`"
                class="leading-[normal] border border-transparent rounded-md p-3 focus-field" />
        </section>

        <template #footer>
            <BaseButton theme="tertiary" @click="isOpen = false">Close</BaseButton>
            <BaseButton theme="primary" data-testid="copy-all-self-review" :disabled="!allText" class="flex items-center gap-1" @click="copy(allText, ALL_KEY)">
                <Copy class="w-3 h-3" aria-hidden="true" />
                {{ isCopied && copiedKey === ALL_KEY ? "Copied!" : "Copy all" }}
            </BaseButton>
        </template>
    </BaseDialog>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseTextarea from "~vue/components/form/BaseTextarea.vue";
import { useTimedShow } from "~vue/composables/useTimedShow";
import Copy from "~vue/icons/Copy.vue";
import { PERFORMANCE_REVIEW_EVENT_PREFIX } from "~vue/Moments/constants";
import type { SelfReviewSectionDraft } from "~vue/types/performance_management";
import { logError, logUserInteraction } from "~vue/utils/logUtils";
import { computed, inject, ref, watch } from "vue";

const ALL_KEY = "__all__";

interface Props {
    sections: SelfReviewSectionDraft[];
}

const props = defineProps<Props>();
const isOpen = defineModel<boolean>("open", { default: false });

const coachingSessionUuid = inject<string | undefined>("coachingSessionUuid", undefined);
const { show: showCopied, isShowing: isCopied } = useTimedShow({ duration: 2000 });
const copiedKey = ref("");

const localSections = ref<SelfReviewSectionDraft[]>([]);

watch(
    () => [props.sections, isOpen.value] as const,
    ([sections, open]) => {
        if (open) {
            localSections.value = sections.map((section) => ({ ...section }));
        }
    },
    { immediate: true },
);

const allText = computed(() =>
    localSections.value
        .filter((section) => section.comment.trim())
        .map((section) => `${section.name}\n${section.comment.trim()}`)
        .join("\n\n"),
);

async function copy(text: string, key: string, sectionIndex?: number) {
    try {
        await navigator.clipboard.writeText(text);
    } catch (e) {
        logError(e);
        return;
    }
    copiedKey.value = key;
    showCopied();
    logUserInteraction(
        `${PERFORMANCE_REVIEW_EVENT_PREFIX}self_review_draft_copied`,
        { session_id: coachingSessionUuid, section_index: sectionIndex ?? null, copy_all: key === ALL_KEY },
        coachingSessionUuid,
    );
}
</script>
